import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyFileFilters,
  browseRoot,
  filesAt,
  folderWithinRoot,
  foldersAt,
  narrowsFiles,
  NO_FILE_FILTERS,
  scopedFilters,
  searchFiles,
  subtreeOf,
  type BrowsableFile,
  hasActiveFilters,
  type FileFilterState,
  type FileFolderRow,
} from './file-browser';
import { readFilesViewPref, writeFilesViewPref } from './files-view-pref';
import {
  mergeFilesUrlState,
  readFilesUrlState,
  sameFilesUrlState,
  type FilesUrlState,
  type FilesViewMode,
} from './files-url-state';

// The Files browser's STATE — which folder you are in, what you have narrowed to, and whether you are looking
// at a list or at thumbnails — over the pure rules in `lib/file-browser.ts`. Both Files surfaces use it (the
// cross-workspace page rooted at `''`, a workspace's own gallery rooted at its id), so the two screens browse
// and filter identically. Mirrors the state the native `FileBrowser` + `FileFilterState` hold.
//
// ## The URL is the source of truth
//
// Every part of it lives in the query string (see `lib/files-url-state.ts`), so a refresh keeps your place, the
// view is shareable as a link, and Back means what it usually means. React state mirrors it for the render;
// the URL is what survives.
//
// ## …except the VIEW, which is a per-device preference and NOT in the URL
//
// List-or-thumbnails is not part of the question you asked of your files, it is how you like to look at them —
// so it lives in `localStorage` (`lib/files-view-pref.ts`), the way both phones keep it, and never in the query
// string. A link carries where you are and what you narrowed to; it must not carry, or overwrite, the taste of
// whoever sent it.
//
// ## Narrowing flattens the view, deliberately
//
// With nothing narrowed the list is a DIRECTORY: the current folder's subfolders, then the files sitting
// directly in it. The moment a filter or a search term is set it becomes a RESULT LIST: every match anywhere
// beneath the current folder, flat, with each row saying which folder it came from.
//
// The alternative — keep showing folder rows and filter only the direct children — answers a question nobody
// asked: a file matches your filter but sits one level down, so the screen tells you there is nothing here
// while the folder row beside it silently holds the match. A filter is a question about your files, not about
// one directory.
//
// ## The workspace level is skipped, and the Space MOVES you rather than narrowing you
//
// The cross-workspace surface's top level is every workspace's own contents merged (`skipsWorkspaceLevel`) —
// their folders and any loose files — instead of a row of workspace folders to open first. Below that it is an
// ordinary directory. The Space is a chip, the same chip Tasks and Routines carry, and picking one makes that
// workspace the root (`browseRoot`) — the same listing, narrowed to one Space.

export interface FilesBrowserState<T extends BrowsableFile> {
  /** The folder being browsed — a full path in the one path space, starting at `root`. */
  folder: string;
  /** Where this browser is rooted right now: the surface's own root, or the Space the user picked
   *  (`browseRoot`). The breadcrumb and the folder-reset both measure against this, not the prop. */
  root: string;
  setFolder: (folder: string) => void;
  filters: FileFilterState;
  setFilters: (filters: FileFilterState) => void;
  view: FilesViewMode;
  setView: (view: FilesViewMode) => void;
  /** True when a filter or the search box is narrowing the list — see the note above. */
  narrowed: boolean;
  /** Subfolder rows to render above the files. Empty while narrowed. */
  folders: FileFolderRow[];
  /** The rows to render: this folder's own files, or every match beneath it while narrowed. */
  files: T[];
  /** Everything beneath the current folder — what the filter option lists are derived from, so the menus can
   *  never offer a value that matches nothing here. */
  scope: T[];
  /** Resets to `root` with no filters. Leaves the view mode alone: how you like to LOOK at files is a
   *  preference, not part of the question you asked. */
  clear: () => void;
}

export function useFilesBrowser<T extends BrowsableFile>(
  items: T[],
  root: string,
  query: string,
  initialFilters?: Partial<FileFilterState>,
): FilesBrowserState<T> {
  const router = useRouter();
  const fromUrl = useMemo(() => readFilesUrlState(router.query), [router.query]);

  const [folder, setFolderState] = useState(fromUrl.folder ?? root);
  // The surface's own starting filters, unless the URL already says otherwise — a shared link and the Back
  // button both describe a state the reader chose, and that outranks the surface's default. Read once: this
  // is a STARTING position, so re-applying it on every render would make the chip impossible to dismiss.
  const [filters, setFiltersState] = useState<FileFilterState>(() =>
    hasActiveFilters(fromUrl.filters) ? fromUrl.filters : { ...fromUrl.filters, ...initialFilters },
  );
  // NOT read from storage during the first render: this page is server-rendered, and a client-only initial value
  // is a hydration mismatch. The mount effect below applies the remembered one instead.
  const [view, setViewState] = useState<FilesViewMode>('list');

  // Where the browser is actually rooted: the surface's own root, or the Space the user picked on the
  // cross-workspace surface. Everything below browses from here rather than from `root`.
  const base = browseRoot(root, filters);

  /** Writes the state into the query, merged with whatever else is there (`?file=` opens the viewer, and the
   *  route's own params must survive). Shallow, so no data is refetched and Back walks the states. */
  const push = useCallback(
    (next: FilesUrlState) => {
      if (!router.isReady) return;
      const merged = mergeFilesUrlState(router.query, next);
      void router.replace({ pathname: router.pathname, query: merged }, undefined, { shallow: true, scroll: false });
    },
    [router],
  );

  const setFolder = useCallback(
    (next: string) => {
      setFolderState(next);
      // A folder AT the browsing root is the default, so it leaves the URL rather than pinning `folder=`.
      push({ folder: next === base ? null : next, filters });
    },
    [push, base, filters],
  );

  const setFilters = useCallback(
    (next: FileFilterState) => {
      setFiltersState(next);
      // Changing the SPACE moves the browsing root, and the folder you were in was addressed against the
      // root you just left — so it does not survive the move. Setting a Space roots you at that Space;
      // CLEARING it returns you to the merged root, which is what the chip going back to "All Spaces" says
      // happened, and what a reader asking for "all spaces" means by it.
      //
      // `folderWithinRoot` alone is not enough, and this is the bug it hid: the merged root is `''`, and
      // EVERY path is "within" the empty root — so clearing the Space left `folder` standing on the
      // workspace it had rooted at, a level the merged root does not draw. The list then showed one
      // Space's files under a chip that said All Spaces, with `?folder=<workspaceId>` pinned in the URL.
      // It used to be survivable because the trail spelled the Space out; §13 dropped that crumb, so
      // there was nothing on screen offering a way back out.
      //
      // Only a base CHANGE resets — narrowing by date or type leaves the folder exactly where it is.
      // Mirrors iOS `.onChange(of: base) { folder = base }` and Android's `LaunchedEffect(base)`.
      const nextBase = browseRoot(root, next);
      const nextFolder = nextBase === base ? folder : nextBase;
      if (nextFolder !== folder) setFolderState(nextFolder);
      push({ folder: nextFolder === nextBase ? null : nextFolder, filters: next });
    },
    [push, root, base, folder],
  );

  // No `push` here: the view is a stored preference, not URL state.
  const setView = useCallback((next: FilesViewMode) => {
    setViewState(next);
    writeFilesViewPref(next);
  }, []);

  // The remembered view, applied once the browser is available.
  useEffect(() => {
    const remembered = readFilesViewPref();
    if (remembered) setViewState(remembered);
  }, []);

  // The URL changing UNDER us — Back/Forward, or a pasted link — is the other direction of the same binding.
  // Guarded on the states being genuinely different, or the write above would bounce back and forth forever.
  useEffect(() => {
    if (!router.isReady) return;
    const current: FilesUrlState = { folder: folder === base ? null : folder, filters };
    if (sameFilesUrlState(current, fromUrl)) return;
    setFolderState(fromUrl.folder ?? browseRoot(root, fromUrl.filters));
    setFiltersState(fromUrl.filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl, router.isReady]);

  // A new base is a different screen (a different workspace), so the folder cannot carry over — it would
  // address a path that no longer exists here and the list would read as empty. `folderWithinRoot` is what
  // decides, because this effect also runs on MOUNT: the cross-workspace screen's root is `''`, and a bare
  // `startsWith('/')` test called every folder foreign and reset a pasted `?folder=…` link to the root.
  useEffect(() => {
    setFolderState((current) => (folderWithinRoot(current, base) ? current : base));
  }, [base]);

  // A folder that no longer holds anything (its last file was moved out, or the list just reloaded without it)
  // would leave the user looking at an empty screen with no way back except the breadcrumb. Fall back to the
  // root instead. Keyed on `items` rather than every render so a folder the user is browsing INTO isn't yanked
  // away mid-navigation.
  useEffect(() => {
    if (folder !== base && items.length > 0 && subtreeOf(items, folder).length === 0) setFolder(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, base]);

  // The folder the render uses. The state can lag the effect above by a frame — on the render where a Space is
  // picked it still holds the old base — and browsing a folder outside the new base would show an empty list.
  const here = folderWithinRoot(folder, base) ? folder : base;
  const scope = useMemo(() => subtreeOf(items, here), [items, here]);
  const narrowed = narrowsFiles(filters, query);

  const files = useMemo(
    () => (narrowed ? applyFileFilters(searchFiles(scope, query), scopedFilters(root, filters)) : filesAt(scope, here, base)),
    [narrowed, scope, query, filters, here, base, root],
  );
  const folders = useMemo(() => (narrowed ? [] : foldersAt(items, here, base)), [narrowed, items, here, base]);

  return {
    folder: here,
    root: base,
    setFolder,
    filters,
    setFilters,
    view,
    setView,
    narrowed,
    folders,
    files,
    scope,
    clear: () => {
      setFiltersState(NO_FILE_FILTERS);
      setFolderState(root);
      push({ folder: null, filters: NO_FILE_FILTERS });
    },
  };
}
